import { formatMarginRequirement } from '@/helpers/ohoHelper';
import { useAppSelector } from '@/state/hooks';
import { selectAccountType } from '@/state/reducers/authSlice';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { selectInstrumentConfigs } from '@/state/reducers/ohoSlice';
import { useCurrency } from '@/utils/hooks/useCurrency';
import { getInstrumentConfig } from '@/utils/hooks/useInstrument';
import { useSso } from '@/utils/providers/SSOProvider';
import { useEffect, useMemo, useState } from 'react';
import { useNumberFormatter } from 'react-aria';
import { findInstrumentInfo, GeneralInfo, InstrumentInfoTab, TradeInfo, useInstrumentInfoTabs } from './InstrumentInfo';
import TabFilters from './TabFilters';

export const formConfig = {
    offsetOverride: { mainAxis: 2, crossAxis: -151 }
};

interface InstrumentInfoContextProps {
    market?: PairMap;
    handleCloseContextMenu: () => void;
}

const InstrumentInfoContext = ({ market, handleCloseContextMenu }: InstrumentInfoContextProps) => {
    const accountType = useAppSelector(selectAccountType);
    const instrumentConfigs = useAppSelector(selectInstrumentConfigs);

    const instrumentInfoTabs = useInstrumentInfoTabs();

    const { jwt, instrumentsIncludeRatioApi, entityType } = useSso();
    const { data: instruments } = instrumentsIncludeRatioApi;

    const isXplorSpot = !location.pathname.includes('/trader');

    const [instrumentInfoContextTab, setInstrumentInfoContextTab] = useState(InstrumentInfoTab.General);
    const [ccy1, ccy2] = useMemo(() => market?.celer.split('/') || [], [market?.celer]);
    const numberFormatter = useNumberFormatter({ minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const ccy1Config = useCurrency(ccy1);
    const ccy2Config = useCurrency(ccy2);

    const publicInstrumentConfig = useMemo(() => {
        return getInstrumentConfig(accountType, instrumentConfigs, market?.celer);
    }, [market, instrumentConfigs, accountType]);

    const detailedInstrumentConfig = useMemo(() => {
        return instruments?.find((instrument) => {
            return findInstrumentInfo(isXplorSpot, publicInstrumentConfig, instrument, market);
        });
    }, [publicInstrumentConfig, instruments, market]);

    const underlyingCcy = useCurrency(detailedInstrumentConfig?.underlying || '');

    const marginRequirement = useMemo(() => {
        return formatMarginRequirement(detailedInstrumentConfig?.marginRequirement, numberFormatter);
    }, [detailedInstrumentConfig]);

    const { isCFD, isRollingFiatFX } = useMemo(
        () => ({
            isCFD: detailedInstrumentConfig?.type === 'CFD',
            isRollingFiatFX: detailedInstrumentConfig?.type === 'FX' && detailedInstrumentConfig?.subType === 'Spot',
            isCryptoSpot: detailedInstrumentConfig?.type === 'Crypto' && detailedInstrumentConfig?.subType === 'Spot'
        }),
        [detailedInstrumentConfig]
    );

    useEffect(() => {
        if (!jwt || !detailedInstrumentConfig) setInstrumentInfoContextTab(InstrumentInfoTab['Trade Info']);
        else setInstrumentInfoContextTab(InstrumentInfoTab.General);
    }, [jwt, detailedInstrumentConfig]);

    return (
        <div className="flex flex-col gap-2 z-[100] cursor-auto text-brand-text">
            <TabFilters.Main>
                {jwt && detailedInstrumentConfig && (
                    <TabFilters.Filter
                        className="!text-xs h-[24px]"
                        selectedFilter={instrumentInfoContextTab}
                        setSelectedFilter={setInstrumentInfoContextTab}
                        filter={instrumentInfoTabs[0]}
                    />
                )}
                <TabFilters.Filter
                    className="!text-xs h-[24px]"
                    selectedFilter={instrumentInfoContextTab}
                    setSelectedFilter={setInstrumentInfoContextTab}
                    filter={instrumentInfoTabs[1]}
                />
            </TabFilters.Main>
            {jwt && detailedInstrumentConfig && instrumentInfoContextTab === InstrumentInfoTab.General && (
                <div className="flex flex-wrap gap-3 p-2 pt-0 justify-between text-xs">
                    <GeneralInfo
                        market={market}
                        detailedInstrumentConfig={detailedInstrumentConfig}
                        underlyingCcy={underlyingCcy}
                        isCFD={isCFD}
                        isRollingFiatFX={isRollingFiatFX}
                    />
                </div>
            )}
            {instrumentInfoContextTab === InstrumentInfoTab['Trade Info'] && (
                <div className="flex flex-wrap gap-3 p-2 pt-0 justify-between text-xs">
                    <TradeInfo
                        publicInstrumentConfig={publicInstrumentConfig}
                        marginRequirement={marginRequirement}
                        entityType={entityType}
                        ccy1Config={ccy1Config}
                        ccy2Config={ccy2Config}
                    />
                </div>
            )}
        </div>
    );
};

export default InstrumentInfoContext;
